import React, { useState } from 'react';
import Column from '@amzn/meridian/column';
import Card from '@amzn/meridian/card';
import Text from '@amzn/meridian/text';
import Alert from '@amzn/meridian/alert';
import Tag from '@amzn/meridian/tag';
import { getAwsConfig } from '../config/appConfig';
import { useAuth } from '../auth/AuthContext';

// One top-level language choice drives every step's snippet.
const LANGS = ['cURL', 'Python', 'Node.js', 'Java'] as const;
type Lang = (typeof LANGS)[number];

/** Dark monospace code block. Scrolls horizontally so long lines never widen
 *  the surrounding layout (this guide renders inside a side sheet). */
function Code({ children }: { children: string }) {
    return (
        <pre
            style={{
                background: '#0f1b2d',
                color: '#e6edf3',
                padding: '12px 14px',
                borderRadius: 8,
                overflowX: 'auto',
                maxWidth: '100%',
                fontSize: 12.5,
                lineHeight: 1.5,
                margin: 0,
                whiteSpace: 'pre',
                boxSizing: 'border-box',
            }}
        >
            <code>{children}</code>
        </pre>
    );
}

/**
 * Post-login guide for the machine-to-machine (M2M) ASN integration channels.
 * Written for a general audience: a plain-English overview, a checklist of
 * what's needed, and copy-ready snippets. A single language selector at the top
 * switches every step's code between cURL / Python / Node.js / Java.
 *
 * Real per-stage endpoints come from runtime config. The signed-in supplier's
 * clientId is shown at the top (safe to display); the clientSecret is issued
 * once at approval and is never exposed to the app.
 */
export default function IntegrationGuide() {
    const [lang, setLang] = useState<Lang>('cURL');
    const { user } = useAuth();

    // Informational page; never let missing/late config crash it.
    let tokenEndpoint = '<oauth-token-endpoint>';
    let apiBase = '<asn-api-endpoint>';
    try {
        const cfg = getAwsConfig();
        tokenEndpoint = cfg.oauthTokenEndpoint ?? tokenEndpoint;
        apiBase = (cfg.asnApiEndpoint ?? apiBase).replace(/\/+$/, '');
    } catch {
        // Runtime config not available; fall through with placeholders.
    }
    const api = apiBase;

    // Use the signed-in supplier's real clientId in the Step 1 snippets when we
    // have it; otherwise keep the generic placeholder. The secret is always a
    // placeholder — it is never available to the app.
    const clientIdSample = user?.clientId ?? '<client_id>';

    // ── Step 1: get an access token ───────────────────────────────────────────
    const tokenSnippets: Record<Lang, string> = {
        'cURL': `curl -X POST ${tokenEndpoint} \\
  -H "Content-Type: application/x-www-form-urlencoded" \\
  -u "${clientIdSample}:<client_secret>" \\
  -d "grant_type=client_credentials&scope=asn/submit"`,
        'Python': `import requests

resp = requests.post(
    "${tokenEndpoint}",
    auth=("${clientIdSample}", "<client_secret>"),
    data={"grant_type": "client_credentials",
          "scope": "asn/submit"},
)
access_token = resp.json()["access_token"]`,
        'Node.js': `const creds = Buffer
  .from("${clientIdSample}:<client_secret>")
  .toString("base64");

const resp = await fetch("${tokenEndpoint}", {
  method: "POST",
  headers: {
    "Content-Type": "application/x-www-form-urlencoded",
    Authorization: \`Basic \${creds}\`,
  },
  body: new URLSearchParams({
    grant_type: "client_credentials",
    scope: "asn/submit",
  }),
});
const { access_token } = await resp.json();`,
        'Java': `String creds = Base64.getEncoder().encodeToString(
    "${clientIdSample}:<client_secret>".getBytes());

HttpRequest req = HttpRequest.newBuilder()
    .uri(URI.create("${tokenEndpoint}"))
    .header("Content-Type",
            "application/x-www-form-urlencoded")
    .header("Authorization", "Basic " + creds)
    .POST(HttpRequest.BodyPublishers.ofString(
        "grant_type=client_credentials"
        + "&scope=asn/submit"))
    .build();

HttpResponse<String> resp = HttpClient.newHttpClient()
    .send(req, HttpResponse.BodyHandlers.ofString());
// parse "access_token" from resp.body()`,
    };

    // ── Step 2: submit a JSON ASN via the API ──────────────────────────────────
    const apiSnippets: Record<Lang, string> = {
        'cURL': `curl -X POST ${api}/v1/asn \\
  -H "Authorization: Bearer <access_token>" \\
  -H "Content-Type: application/json" \\
  --data @asn.json

# 202 Accepted:
# { "status": "ACCEPTED",
#   "submissionId": "...",
#   "asnNumber": "ASN-2026-000123456",
#   "receivedAt": "..." }`,
        'Python': `import json, requests

with open("asn.json") as f:
    asn = json.load(f)

resp = requests.post(
    "${api}/v1/asn",
    headers={"Authorization": f"Bearer {access_token}"},
    json=asn,
)
print(resp.status_code, resp.json())
# 202, { submissionId, asnNumber, ... }`,
        'Node.js': `import { readFile } from "node:fs/promises";

const asn = JSON.parse(
  await readFile("asn.json", "utf8"));

const resp = await fetch("${api}/v1/asn", {
  method: "POST",
  headers: {
    Authorization: \`Bearer \${access_token}\`,
    "Content-Type": "application/json",
  },
  body: JSON.stringify(asn),
});
console.log(resp.status, await resp.json());
// 202, { submissionId, ... }`,
        'Java': `String asn = Files.readString(
    Path.of("asn.json"));

HttpRequest req = HttpRequest.newBuilder()
    .uri(URI.create("${api}/v1/asn"))
    .header("Authorization", "Bearer " + accessToken)
    .header("Content-Type", "application/json")
    .POST(HttpRequest.BodyPublishers.ofString(asn))
    .build();

HttpResponse<String> resp = HttpClient.newHttpClient()
    .send(req, HttpResponse.BodyHandlers.ofString());
// 202: { submissionId, asnNumber, receivedAt }`,
    };

    // ── Step 3: upload a file via presigned URL ─────────────────────────────────
    const presignSnippets: Record<Lang, string> = {
        'cURL': `# 1) Ask for a short-lived upload URL
curl -X POST ${api}/v1/asn/presign \\
  -H "Authorization: Bearer <access_token>" \\
  -H "Content-Type: application/json" \\
  -d '{"fileName":"asn-123.csv",
       "contentType":"text/csv"}'
# -> { "uploadUrl": "https://...",
#      "s3Key": "...",
#      "expiresInSeconds": 900 }

# 2) Upload the file to the returned uploadUrl
curl -X PUT "<uploadUrl>" \\
  -H "Content-Type: text/csv" \\
  --data-binary @asn-123.csv`,
        'Python': `import requests

# 1) Ask for a presigned upload URL
presign = requests.post(
    "${api}/v1/asn/presign",
    headers={"Authorization": f"Bearer {access_token}"},
    json={"fileName": "asn-123.csv",
          "contentType": "text/csv"},
).json()

# 2) Upload the file straight to storage
with open("asn-123.csv", "rb") as f:
    requests.put(
        presign["uploadUrl"],
        data=f,
        headers={"Content-Type": "text/csv"},
    )`,
        'Node.js': `import { readFile } from "node:fs/promises";

// 1) Ask for a presigned upload URL
const presign = await (await fetch(
  "${api}/v1/asn/presign",
  {
    method: "POST",
    headers: {
      Authorization: \`Bearer \${access_token}\`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      fileName: "asn-123.csv",
      contentType: "text/csv",
    }),
  },
)).json();

// 2) Upload the file straight to storage
await fetch(presign.uploadUrl, {
  method: "PUT",
  headers: { "Content-Type": "text/csv" },
  body: await readFile("asn-123.csv"),
});`,
        'Java': `// 1) Ask for a presigned upload URL
HttpRequest presignReq = HttpRequest.newBuilder()
    .uri(URI.create("${api}/v1/asn/presign"))
    .header("Authorization", "Bearer " + accessToken)
    .header("Content-Type", "application/json")
    .POST(HttpRequest.BodyPublishers.ofString(
        "{\\"fileName\\":\\"asn-123.csv\\","
        + "\\"contentType\\":\\"text/csv\\"}"))
    .build();
String uploadUrl = /* parse "uploadUrl" */ "";

// 2) Upload the file straight to storage
HttpRequest putReq = HttpRequest.newBuilder()
    .uri(URI.create(uploadUrl))
    .header("Content-Type", "text/csv")
    .PUT(HttpRequest.BodyPublishers.ofFile(
        Path.of("asn-123.csv")))
    .build();
HttpClient.newHttpClient()
    .send(putReq, HttpResponse.BodyHandlers.ofString());`,
    };

    return (
        <Column spacing="400" width="100%" maxWidth={620}>
            <Text type="b200" color="secondary">
                An ASN (Advance Shipping Notice) tells us what you&rsquo;re shipping before it
                arrives. If you want <strong>programmatic access</strong> — sending ASNs directly
                from your own systems, without the manual upload screens — please follow this guide.
                It walks you through authenticating and submitting over either of two channels; pick
                the one that fits your data. You only need one.
            </Text>

            {/* Your credentials — specific to this signed-in login. The clientId
                is safe to show; the secret is issued once at approval and is
                never displayed here. */}
            <Card spacingInset="500">
                <Column spacing="200">
                    <Text type="h300">Your credentials</Text>
                    {user ? (
                        <Column spacing="100">
                            <Text type="b200" color="secondary">
                                Signed in as <strong>{user.email}</strong>
                                {user.company ? (
                                    <>
                                        {' '}
                                        (<strong>{user.company}</strong>)
                                    </>
                                ) : null}
                                . The credentials below are specific to this login.
                            </Text>
                            <Text type="b200" color="secondary">
                                <strong>clientId</strong>
                            </Text>
                            {user.clientId ? (
                                <Code>{user.clientId}</Code>
                            ) : (
                                <Text type="b200" color="secondary">
                                    Your <code>clientId</code> is issued when your account is
                                    approved. If you don&rsquo;t have it yet, request it from{' '}
                                    <strong>fortumel@amazon.com</strong>.
                                </Text>
                            )}
                            <Text type="b200" color="secondary">
                                <strong>clientSecret</strong>
                            </Text>
                            <Alert type="warning" size="small">
                                For your security, the <code>clientSecret</code> is shown only once,
                                when your account is approved. It is never displayed here. If you
                                have lost it or it may be exposed, ask{' '}
                                <strong>fortumel@amazon.com</strong> to rotate it and re-issue a new
                                secret.
                            </Alert>
                        </Column>
                    ) : (
                        <Text type="b200" color="secondary">
                            Sign in to see the <code>clientId</code> issued to your organization.
                        </Text>
                    )}
                </Column>
            </Card>

            {/* Prerequisites */}
            <Card spacingInset="500">
                <Column spacing="200">
                    <Text type="h300">Before you begin</Text>
                    <Text type="b200" color="secondary">
                        You must pass a <strong>clientId</strong> and a <strong>clientSecret</strong>{' '}
                        on every integration. These are issued to your organization by the Supplier
                        Connect team — request them by emailing{' '}
                        <strong>fortumel@amazon.com</strong>. You will not be able to authenticate
                        (Step 1) without both values.
                    </Text>
                    <Text type="b200" color="secondary">
                        Keep the <code>clientSecret</code> confidential: store it in a secrets
                        manager, never commit it to source control, and rotate it immediately if it
                        is ever exposed.
                    </Text>
                    <Text type="b200" color="secondary">
                        You will also need one of the following, plus the ability to make HTTPS
                        requests from your system:
                    </Text>
                    <Column spacing="100">
                        <Text type="b200" color="secondary">
                            • <strong>Channel A</strong> — your ASN as a JSON body, submitted in a
                            single API call.
                        </Text>
                        <Text type="b200" color="secondary">
                            • <strong>Channel B</strong> — a <code>.json</code> or <code>.csv</code>{' '}
                            file, uploaded in a 2-step process where you supply the exact file name.
                        </Text>
                    </Column>
                </Column>
            </Card>

            {/* Language selector — drives every code sample below. */}
            <Column spacing="100">
                <Text type="b100" color="secondary">
                    Show examples in
                </Text>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {LANGS.map((l) => {
                        const active = l === lang;
                        return (
                            <button
                                key={l}
                                type="button"
                                onClick={() => setLang(l)}
                                style={{
                                    padding: '6px 14px',
                                    borderRadius: 6,
                                    border: `1px solid ${active ? '#0C6ECA' : '#DDE1E6'}`,
                                    background: active ? '#0C6ECA' : '#fff',
                                    color: active ? '#fff' : '#4B5563',
                                    fontSize: 13,
                                    fontWeight: active ? 700 : 500,
                                    cursor: 'pointer',
                                }}
                            >
                                {l}
                            </button>
                        );
                    })}
                </div>
            </Column>

            {/* Step 1 — shared prerequisite for both channels. */}
            <Card spacingInset="500">
                <Column spacing="300">
                    <Text type="h300">Step 1 — Get an access token</Text>
                    <Text type="b200" color="secondary">
                        Both channels authenticate the same way. Exchange the{' '}
                        <strong>clientId</strong> and <strong>clientSecret</strong> you received from{' '}
                        <strong>fortumel@amazon.com</strong> for a short-lived bearer token using the
                        OAuth2 client-credentials grant with the <code>asn/submit</code> scope. Send
                        this token in the <code>Authorization: Bearer</code> header on every request.
                        Tokens expire in one hour — request a new one when it does.
                    </Text>
                    <Code>{tokenSnippets[lang]}</Code>
                </Column>
            </Card>

            <Text type="h400">Step 2 — Send your ASN (choose one channel)</Text>

            {/* Channel A: JSON via API */}
            <Card spacingInset="500">
                <Column spacing="300">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Tag type="theme">Channel A</Tag>
                        <Text type="h300">Submit JSON via the API</Text>
                    </div>
                    <Text type="b200" color="secondary">
                        A <strong>single request</strong>. POST your ASN as a JSON body to{' '}
                        <code>/v1/asn</code>. We validate it against the ASN schema synchronously and
                        respond immediately:
                    </Text>
                    <Column spacing="100">
                        <Text type="b200" color="secondary">
                            • <strong>202 Accepted</strong> — the ASN passed validation. The response
                            includes a <code>submissionId</code> and the <code>asnNumber</code>.
                        </Text>
                        <Text type="b200" color="secondary">
                            • <strong>400 Rejected</strong> — validation failed. The response lists
                            the errors so you can correct and resubmit.
                        </Text>
                    </Column>
                    <Text type="b200" color="secondary">
                        Best for small, structured payloads generated directly by your system.
                    </Text>
                    <Code>{apiSnippets[lang]}</Code>
                </Column>
            </Card>

            {/* Channel B: file via presigned URL */}
            <Card spacingInset="500">
                <Column spacing="300">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Tag type="theme">Channel B</Tag>
                        <Text type="h300">Upload a file via a presigned URL</Text>
                    </div>
                    <Text type="b200" color="secondary">
                        A <strong>2-step</strong> process. Best for larger payloads and for CSV or
                        Excel exports. In this channel you provide the <code>fileName</code>, and it
                        is verified during the upload — so the file name must be given correctly.
                    </Text>
                    <Column spacing="100">
                        <Text type="b200" color="secondary">
                            <strong>Step 2a — Request an upload URL.</strong> POST to{' '}
                            <code>/v1/asn/presign</code> and pass the exact <code>fileName</code> you
                            intend to upload (for example <code>asn-123.csv</code>), along with its{' '}
                            <code>contentType</code>. You get back a one-time <code>uploadUrl</code>{' '}
                            (valid ~15 minutes) and a <code>submissionId</code>.
                        </Text>
                        <Text type="b200" color="secondary">
                            <strong>Step 2b — Upload the file.</strong> Send the file to that{' '}
                            <code>uploadUrl</code> with an HTTP <code>PUT</code>. A background job
                            then validates the file and updates its status (visible in the ASN
                            screen).
                        </Text>
                    </Column>
                    <Alert type="warning" size="small">
                        <strong>Get the file name right.</strong> AWS verifies the{' '}
                        <code>fileName</code> during the upload, so it must be correct:
                        <br />
                        • It must end in <code>.json</code> or <code>.csv</code> — the extension
                        tells us how to parse the file. A missing or wrong extension is rejected with{' '}
                        <code>400</code>.
                        <br />• The <code>contentType</code> you send must match the extension
                        (<code>application/json</code> for <code>.json</code>,{' '}
                        <code>text/csv</code> for <code>.csv</code>). The upload URL is bound to it,
                        so a mismatch makes the <code>PUT</code> fail.
                        <br />• Use the <em>same</em> <code>fileName</code> and{' '}
                        <code>contentType</code> in Step 2a and Step 2b.
                    </Alert>
                    <Code>{presignSnippets[lang]}</Code>
                </Column>
            </Card>
        </Column>
    );
}
